/**
 * Pure helpers for the sample-selector dropdown on the sample detail page.
 * Kept separate from the SampleDetails component so they can be unit-tested in isolation.
 */

/**
 * Safely convert an ID (or an object with _id) to a string for comparison.
 * @param {*} val - An ObjectId, string, or object carrying an _id
 * @returns {string} String form of the id, or '' when nullish
 */
export const toIdString = (val) => {
  if (val == null) return '';
  const id = val?._id ?? val;
  return id?.toString?.() ?? String(id);
};

/**
 * Decide whether a dropdown option represents the same revision chain as the opened sample.
 * Options are keyed on the latest version's _id, so an opened older version must match via the
 * chain's member ids or its revision root key rather than an exact _id compare.
 * @param {object} option - Dropdown option (may carry revisionMemberIds and revisionRootKey)
 * @param {object} openedSample - The currently opened sample (sampleDetailsRedux)
 * @returns {boolean} True when the option belongs to the opened sample's revision chain
 */
export const matchOptionToOpenedSample = (option, openedSample) => {
  if (!option || !openedSample) return false;
  const openedId = toIdString(openedSample?._id);
  if (openedId && toIdString(option._id) === openedId) return true;
  if (openedId && option.revisionMemberIds?.some((memberId) => toIdString(memberId) === openedId)) return true;
  const openedRoot = toIdString(openedSample?.revisionRootId ?? openedSample?._id);
  return Boolean(openedRoot) && toIdString(option.revisionRootKey) === openedRoot;
};

/**
 * Compute the small badge value shown next to a dropdown option.
 * Non-automation groups have no stage chain, so show the sample's own ref number (sampleNumber);
 * automation options keep the version count / version number behaviour.
 * @param {object} option - Dropdown option
 * @returns {string|number} Badge text ('' when nothing applies)
 */
export const getSampleBadgeValue = (option) => {
  if (option?.isNonAutomation) return option?.sampleNumber ?? '';
  const count = option?.versionCount;
  return count > 1 ? count : option?.versionNumber ?? '';
};
